import { Flex, Center, Box, Spinner, useToast } from '@chakra-ui/react';
import { useQueries } from '@tanstack/react-query';
import { ScheduleAPI } from '@/components/shared/interview-scheduler/api';
import {
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { FormBuilder } from '@/components/formBuilder';
import { useEmailField } from '@/components/formBuilder/formFields/useEmailField';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { templatesCategoryConfig } from '@/constants/templates-category';
import { components } from 'react-select';
import { LayoutLeft } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components';
import { useShowToast } from '@/hooks';
import { SCHEDULE_TYPE_MANUAL } from '@/components/shared/interview-scheduler/constant';
import { convertPlainTextToHtml } from '@/utils/textarea-parser';
import { useKFlags } from '@/hooks/use-k-flags';

const TEMPLATE_QUERIES_ARRAY = [
  {
    queryParams: {
      channel: 'calendar_invite',
      category: 'candidate_interview_notice',
    },
    queryKey: ['getCandidateEmailTemplates'],
  },
  {
    queryParams: { channel: 'calendar_invite', category: 'interviewer_notice' },
    queryKey: ['getInterviewerEmailTemplates'],
  },
];

interface CalendarInvitesProps {
  interviewPlan: any;
  containerHeight: any;
  previewPayload: any;
  scheduleType: string;
  previewDetails?: any;
}

export const CalendarInvites = forwardRef(
  (
    {
      interviewPlan,
      containerHeight,
      previewPayload,
      scheduleType,
      previewDetails,
    }: CalendarInvitesProps,
    ref
  ) => {
    const toastRef = useToast();
    const toast = useShowToast();
    const candidateRef = useRef(null);
    const interviewRef = useRef(null);
    const [candidateTemplates, setCandidateTemplates] = useState(null);
    const [interviewTemplates, setInterviewTemplates] = useState(null);
    const [canSelectedTemplate, setCanSelectedTemplate] = useState(null);
    const [intSelectedTemplate, setIntSelectedTemplate] = useState(null);
    const [candidateIsPreview, setCandidateIsPreview] = useState(false);
    const [interviewerIsPreview, setInterviewerIsPreview] = useState(false);
    const { feHtmlToText } = useKFlags();

    const interviewerView = templatesCategoryConfig.interviewer_notice.view;
    const {
      data: { variables: interviewerVariables = [] } = {},
      isLoading: isLoadingVariables,
    } = useGetTemplatesVariablesQuery({
      params: { view: interviewerView },
    });

    const templateQueries = useQueries({
      queries: TEMPLATE_QUERIES_ARRAY.map((itr) => ({
        queryKey: itr.queryKey,
        queryFn: () => ScheduleAPI?.getCalendarInviteTemplates(itr.queryParams),
        staleTime: 2 * 60 * 1000, // 2 minutes
      })),
      combine: (results) => {
        return {
          data: results.map((result) => result.data),
          isLoading: results.some((result) => result.isPending),
        };
      },
    });

    const handleCandidateTemplateChange = (value) => {
      setCanSelectedTemplate(value);
    };

    const handleInterviewerTemplateChange = (value) => {
      setIntSelectedTemplate(value);
    };

    const CustomValueContainer = ({ children, ...props }) => (
      <components.ValueContainer {...props}>
        <Flex alignItems="center" gap="4px">
          <LayoutLeft
            size="1em"
            color="var(--kula-colors-secondary-700)"
            className="kula-icon"
          />
          <Box>{children}</Box>
        </Flex>
      </components.ValueContainer>
    );

    const getEmailLabel = (type) => {
      const label =
        type === 'candidate'
          ? `Candidate's calendar invite`
          : `Interviewer's calendar invite`;
      return (
        <KText
          color="secondary-900"
          size="md-medium"
          data-test-id={`text-${type}-calendar-invite`}
        >
          {label}
        </KText>
      );
    };

    const editorContainerHeight = useMemo((): number => {
      let headerHeight = 130; // header height
      if (scheduleType !== SCHEDULE_TYPE_MANUAL) {
        headerHeight = 170; // header height + info banner height
      }
      return containerHeight - headerHeight;
    }, [containerHeight, scheduleType]);

    const setTemplatesWithSelection = () => {
      // set template default and selected values
      const candidateTemplates = templateQueries?.data?.[0] || [];
      const interviewTemplates = templateQueries?.data?.[1] || [];
      setCandidateTemplates(candidateTemplates);
      setInterviewTemplates(interviewTemplates);
      // If templates are available from the interview plan, use those. Otherwise, select the first template from the default list.
      let candidateTemplate =
        interviewPlan?.candidate_comm_template || candidateTemplates?.[0] || {};
      let interviewTemplate =
        interviewPlan?.reviewer_comm_template || interviewTemplates?.[0] || {};

      // // Convert plain text to HTML for edit case
      if (candidateTemplate?.body) {
        let updatedBody = candidateTemplate?.body;
        if (feHtmlToText) {
          updatedBody = convertPlainTextToHtml(updatedBody || '');
        }
        candidateTemplate = {
          ...candidateTemplate,
          body: updatedBody,
        };
      }
      if (interviewTemplate?.body) {
        let updatedBody = interviewTemplate?.body;
        if (feHtmlToText) {
          updatedBody = convertPlainTextToHtml(updatedBody || '');
        }
        interviewTemplate = {
          ...interviewTemplate,
          body: updatedBody,
        };
      }

      setCanSelectedTemplate(candidateTemplate);
      setIntSelectedTemplate(interviewTemplate);
    };

    useEffect(() => {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing; effect restructure is out of scope for this constants move
      setTemplatesWithSelection();
    }, [templateQueries]); // eslint-disable-line react-hooks/exhaustive-deps

    const candidateEmailFieldConfigs = useEmailField({
      formRef: candidateRef,
      isReplyMode: true,
      showFromField: false,
      bodyLabel: '',
      templateData: canSelectedTemplate,
      variables: interviewerVariables,
      getEditorInstance: true,
      replaceContent: true,
      pluginsConfig: ['link'],
      containerPropStyles: {
        borderTop: '1px solid',
        borderColor: 'secondary-100',
        borderRadius: '6px',
        h: editorContainerHeight + 'px',
        overflow: 'auto',
      },
      editorHeight: editorContainerHeight,
      previewProps: {
        payload: previewPayload,
        enable: true,
        onClick: (action) => {
          setCandidateIsPreview(action);
        },
        previewDetails: previewDetails?.candidate,
      },
      toolBarProps: {
        top: {
          position: 'sticky',
          top: 0,
        },
      },
    });

    const interviewEmailFieldConfigs = useEmailField({
      formRef: interviewRef,
      isReplyMode: true,
      showFromField: false,
      bodyLabel: '',
      templateData: intSelectedTemplate,
      variables: interviewerVariables,
      getEditorInstance: true,
      replaceContent: true,
      pluginsConfig: ['link'],
      containerPropStyles: {
        borderTop: '1px solid',
        borderColor: 'secondary-100',
        borderRadius: '6px',
        h: editorContainerHeight + 'px',
        overflow: 'auto',
      },
      editorHeight: editorContainerHeight,
      previewProps: {
        payload: previewPayload,
        enable: true,
        onClick: (action) => {
          setInterviewerIsPreview(action);
        },
        previewDetails: previewDetails?.interviewer,
      },
      toolBarProps: {
        top: {
          position: 'sticky',
          top: 0,
        },
      },
    });

    const candidateInvitesFormConfig = [
      {
        type: 'wrapper',
        Wrapper: Box,
        wrapperProps: { padding: '15px 20px', width: '100%' },
        fields: [
          {
            type: 'custom',
            Field: () => {
              return getEmailLabel('candidate');
            },
          },
          {
            name: 'candidate_template',
            type: 'autoComplete',
            useInput: true,
            isRequired: true,
            isMulti: false,
            placeholder: `Select a template`,
            labelKey: 'name',
            valueKey: 'id',
            customOnChange: handleCandidateTemplateChange,
            isDisabled: candidateIsPreview,
            options: candidateTemplates?.map?.((i) => ({
              label: i?.name,
              value: i?.id,
              id: i?.id,
              ...i,
            })),
            updateEndpointOnChange: true,
            fetchConfig: {
              method: 'get',
              endPoint: `ats_comm_templates?filter_by[channel]=calendar_invite&filter_by[category][]=candidate_interview_notice&items=200&filter_by[name]=`,
              processResponse: (res) => {
                const options = res?.data?.map((item) => ({
                  label: item?.name,
                  value: item?.id,
                  ...item,
                }));

                return options;
              },
            },
            components: {
              ValueContainer: CustomValueContainer,
              PlaceholderContainer: ({ children, ...props }) => (
                <components.Placeholder
                  {...props}
                  style={{ paddingLeft: '10px' }}
                >
                  {children}
                </components.Placeholder>
              ),
            },
          },
          ...candidateEmailFieldConfigs,
        ],
      },
    ];

    const interviewInvitesFormConfig = [
      {
        type: 'wrapper',
        Wrapper: Box,
        wrapperProps: { padding: '15px 20px', width: '100%' },
        fields: [
          {
            type: 'custom',
            wrapperProps: {
              display: 'flex',
            },
            Field: () => {
              return getEmailLabel('interview');
            },
          },
          {
            name: 'interviewer_template',
            type: 'autoComplete',
            useInput: true,
            isRequired: true,
            isMulti: false,
            placeholder: `Select a template`,
            labelKey: 'name',
            valueKey: 'id',
            customOnChange: handleInterviewerTemplateChange,
            isDisabled: interviewerIsPreview,
            options: interviewTemplates?.map?.((i) => ({
              label: i?.name,
              value: i?.id,
              id: i?.id,
              ...i,
            })),
            updateEndpointOnChange: true,
            fetchConfig: {
              method: 'get',
              endPoint: `ats_comm_templates?filter_by[channel]=calendar_invite&filter_by[category][]=interviewer_notice&items=200&filter_by[name]=`,
              processResponse: (res) => {
                const options = res?.data?.map((item) => ({
                  label: item?.name,
                  value: item?.id,
                  ...item,
                }));

                return options;
              },
            },
            components: {
              ValueContainer: CustomValueContainer,
              PlaceholderContainer: ({ children, ...props }) => (
                <components.Placeholder
                  {...props}
                  style={{ paddingLeft: '10px' }}
                >
                  {children}
                </components.Placeholder>
              ),
            },
          },
          ...interviewEmailFieldConfigs,
        ],
      },
    ];

    const getModel = async () => {
      const containerElement = document.querySelector(
        '.schedule-calendar-invites-container'
      );
      const hasErrorVariable = containerElement?.querySelector(
        '.empty-variable-alert-container'
      );

      if (hasErrorVariable) {
        toastRef?.closeAll();
        toast({
          title: 'Empty variables detected',
          description:
            'The variables marked in red do not have any data. Remove them to continue',
          status: 'error',
        });
        document
          .querySelector('.unsupported')
          .scrollIntoView({ block: 'start' });
      }
      const candidate_description = candidateRef?.current?.values?.body || '';
      const interviewer_description = interviewRef?.current?.values?.body || '';

      return {
        hasValidData: !hasErrorVariable,
        payload: {
          candidate_description,
          interviewer_description,
        },
      };
    };

    useImperativeHandle(ref, () => ({
      getModel,
    }));

    if (templateQueries.isLoading) {
      return (
        <Center h="100%" w="100%">
          <Spinner color="primary-800" />
        </Center>
      );
    }

    const canInitialValues = {
      candidate_template: canSelectedTemplate?.id,
      body: feHtmlToText
        ? convertPlainTextToHtml(canSelectedTemplate?.body || '')
        : canSelectedTemplate?.body,
    };
    const intInitialValues = {
      interview_template: intSelectedTemplate?.id,
      body: feHtmlToText
        ? convertPlainTextToHtml(intSelectedTemplate?.body || '')
        : intSelectedTemplate?.body,
    };

    if (isLoadingVariables) {
      return (
        <Flex justifyContent="center" alignItems="center" h="100%" w="100%">
          <Spinner />
        </Flex>
      );
    }

    return (
      <Flex
        position={'relative'}
        width={'100%'}
        height={'100%'}
        data-test-id="section-calendar-invites"
        className="schedule-calendar-invites-container"
      >
        <Flex width={'100%'} height={'100%'}>
          <Flex flex={1} data-test-id="section-candidate-calendar-invite">
            <FormBuilder
              config={candidateInvitesFormConfig}
              formRef={candidateRef}
              initialValues={canInitialValues}
              showCustomError={false}
              isObserveEnabled={true}
              formContainerProps={{ width: '100%' }}
            />
          </Flex>
          <Flex flex={1} data-test-id="section-interviewer-calendar-invite">
            <FormBuilder
              config={interviewInvitesFormConfig}
              formRef={interviewRef}
              initialValues={intInitialValues}
              showCustomError={false}
              isObserveEnabled={true}
              formContainerProps={{ width: '100%' }}
            />
          </Flex>
        </Flex>
        <Box
          position={'absolute'}
          left={'50%'}
          w={'1px'}
          h={'100%'}
          bg={'secondary-100'}
        ></Box>
      </Flex>
    );
  }
);

CalendarInvites.displayName = 'CalendarInvites';
